import { Money, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator, Text, Tooltip } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { PurposePerformanceDocument } from './graphql/purposePerformance.generated';

import { urlBuilder } from '../../../Shared/urlBuilder';

const calculatedTotalEarned = (investing?: number, interest?: number) => {
  let total = 0;

  if (investing !== undefined && investing !== null) {
    total += investing;
  }

  if (interest !== undefined && interest !== null) {
    total += interest;
  }
  return Money(total);
};

export const PerformanceCardView: React.FC = () => {
  const purposeId = useRequiredParam('purposeId');
  const { enabled: isReactifyPerformanceEnabled, loading: isReactifyPerformanceLoading } = useFeatureFlag({
    splitName: 'retail.user_level_performance_reactify_q3_2025_enabled',
    context: 'PurposePerformanceCard',
  });

  const { data } = useGraphQLQuery(PurposePerformanceDocument, {
    variables: { id: purposeId },
    throwOnError: true,
  });

  if (!data || isReactifyPerformanceLoading) return <CardLoader />;
  if (!data.purpose.envelope.performanceTotals) return null;

  const { investing, interest } = data.purpose.envelope.performanceTotals;
  const { legacyGoalId } = data.purpose;
  const { id: accountId } = data.purpose.envelope.accounts[0]; // Assumes that the purpose has exactly one account

  if (!investing && !interest) return null;

  const totalEarned = calculatedTotalEarned(investing?.earned.cents, interest?.paid.cents);

  const performanceUrl = isReactifyPerformanceEnabled
    ? urlBuilder.appAccountPerformance.absolute({
        accountId,
        returnTo: urlBuilder.appPurpose.absolute({ purposeId }),
        returnLabel: 'Account details',
      })
    : urlBuilder.appPerformance.absolute({ 'settings[goal_ids][]': legacyGoalId });

  return (
    <Card
      css={{ padding: 'space.300', display: 'grid', gap: 'space.300' }}
      data-testid="accountOverviewDetailOrPerformance"
    >
      <Card.Header css={{ display: 'flex', justifyContent: 'space-between' }}>
        <H2 variant="title200">Performance</H2>
        <SambaAutoLink to={performanceUrl}>See details</SambaAutoLink>
      </Card.Header>
      <Separator />
      <Card.Content css={{ gap: 'space.200', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {totalEarned && (
          <Box css={{ display: 'grid' }}>
            <Text variant="text50">Total earned</Text>
            <Text variant="text200" color={totalEarned.cents > 0 ? 'text.success' : 'text'}>
              {formatMoney(totalEarned, totalEarned.cents > 0 ? 'credit' : 'debit')}
            </Text>
          </Box>
        )}
        {interest?.accrued && (
          <Box css={{ display: 'grid' }}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.50' }}>
              <Text variant="text50">Interest to be paid</Text>
              <Tooltip>
                <Tooltip.Trigger asChild={true}>
                  <Tooltip.IconButton />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  Interest will accrue daily and be credited on the first business day of each month. Credited interest
                  may be rounded to the nearest cent.
                </Tooltip.Content>
              </Tooltip>
            </Box>
            <Text variant="text200" color={interest.accrued.cents > 0 ? 'text.success' : 'text'}>
              {formatMoney(interest.accrued)}
            </Text>
          </Box>
        )}
        {investing?.dividends && (
          <Box css={{ display: 'grid' }}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.50' }}>
              <Text variant="text50">Dividends earned</Text>
              <Tooltip>
                <Tooltip.Trigger asChild={true}>
                  <Tooltip.IconButton />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  Dividends are your portion of a company's earnings. We reinvest available dividends back into your
                  portfolio to boost your total returns.
                </Tooltip.Content>
              </Tooltip>
            </Box>
            <Text variant="text200" color={investing.dividends.total.cents > 0 ? 'text.success' : 'text'}>
              {formatMoney(investing.dividends.total)}
            </Text>
          </Box>
        )}
      </Card.Content>
    </Card>
  );
};

export const PerformanceCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <PerformanceCardView />
  </ErrorBoundary>
);
